import React, { useState, useEffect } from "react";
import "./MarketInsight.css";
import confetti from "canvas-confetti";
import awlLogo from "./awl-logo.png";
import { db } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const MarketInsight = () => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState("");

  const isButtonDisabled = !title.trim() || !category.trim();

  useEffect(() => {
    if (isButtonDisabled) {
      const missingFields = [];
      if (!title.trim()) missingFields.push("Observation");
      if (!category.trim()) missingFields.push("Category");
      setTooltipMessage(`Please fill in: ${missingFields.join(", ")}`);
    } else {
      setTooltipMessage("");
    }
  }, [title, category, isButtonDisabled]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async () => {
    if (isButtonDisabled) return;
    try {
      await addDoc(collection(db, "insights_test"), {
        title,
        date: serverTimestamp(),
        status: "Pending",
      });
      setShowModal(true);
      confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
      setTitle("");
      setCategory("");
      setFile(null);
      setPreviewUrl(null);
    } catch (err) {
      alert("Failed to submit insight. Please try again.");
      console.error("Error adding insight:", err);
    }
  };

  return (
    <div>
      <div className="app-header">
        <h1 className="app-name-header">
          insight<span className="app-name-highlight">360</span>
        </h1>
        <img src={awlLogo} alt="AWL Logo" className="company-logo-header" />
      </div>

      <div className="main-content insight-page">
        <div className="page-intro">
          <h2 className="page-title">Share a New Insight</h2>
          <p className="page-subtitle">
            Your observations drive our success. Let's get started!
          </p>
        </div>

        <form className="insight-form-card">
          <div className="form-group">
            <div className="step-header">
              <h4>Observation Details</h4>
            </div>
            <label htmlFor="observation-category">
              Category <span className="required">*</span>
            </label>
            <select
              id="observation-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="competitor-pricing">Product</option>
              <option value="display">Pricing</option>
              <option value="new-launch">Marketing</option>
              <option value="feedback">Media</option>
              <option value="feedback">BTL Activities</option>
              <option value="feedback">Others</option>
            </select>
          </div>
          <div className="form-step">
            <div className="form-group">
              <label htmlFor="observation-title">
                What's your observation? <span className="required">*</span>
              </label>
              <input
                id="observation-title"
                type="text"
                placeholder="e.g., Competitor BOGO on ₹99 combo pack"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="observation-details">
                Tell us more (optional)
              </label>
              <textarea
                id="observation-details"
                placeholder="Share details..."
                rows="4"
              ></textarea>
            </div>
          </div>

          <div className="form-step">
            <div className="step-header">
              <h4>Capture / Upload Image (Optional)</h4>
            </div>
            <div className="capture-buttons">
              <label className="capture-btn">
                <div className="icon">
                  <svg
                    xmlns="http://www.w.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
                    <circle cx="12" cy="13" r="3"></circle>
                  </svg>
                </div>
                <span>Capture</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </label>
              <label className="capture-btn">
                <div className="icon">
                  <svg
                    xmlns="http://www.w.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <span>Upload</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </label>
            </div>
            {previewUrl && (
              <div className="preview">
                {file.type.startsWith("image/") ? (
                  <img src={previewUrl} alt="Preview" />
                ) : (
                  <video src={previewUrl} controls />
                )}
              </div>
            )}
          </div>

          <div
            className="submit-wrapper"
            data-tooltip={isButtonDisabled ? tooltipMessage : ""}
          >
            <button
              type="button"
              className="submit-btn"
              onClick={handleSubmit}
              disabled={isButtonDisabled}
            >
              Submit Insight
            </button>
          </div>
        </form>
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <button
              className="close-modal-btn"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <h2>Insight Submitted 🎉</h2>
            <p>Your insight is pending for approval!</p>
            <button className="btn-primary" onClick={() => setShowModal(false)}>
              Great!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketInsight;
